import { getCurrentLocale } from '@/shared/helpers/getCurrentLocale';
import { Log } from '@/shared/Log';

/**
 * Formats a number according to the current locale.
 *
 * - Rounds the number to the specified `maxDecimals`.
 * - Removes any trailing zeros after the decimal separator.
 * - Respects locale-specific formatting (e.g., "16,3" in Danish, "16.3" in English).
 *
 * @param value - The number to format.
 * @param minDecimals - Minimum number of decimal places to display (default = 0).
 *                      If greater than 0, the output is padded with zeros up to this precision.
 * @param maxDecimals - Maximum number of decimal places to display (default = 1).
 *                      The value will be rounded to this precision.
 *
 * @returns A locale-aware, human-readable string representation of the number.
 *
 * @example
 * formatNumber(95);          // "95"
 * formatNumber(95.2);        // "95,2" in da-DK
 * formatNumber(16.33);       // "16,3" in da-DK (with maxDecimals = 1)
 * formatNumber(16.33, 0, 2); // "16,33"
 */

export function formatNumber(
    value: number | null,
    minDecimals = 0,
    maxDecimals = 1,
): string | null {
    try {
        if (typeof value !== 'number' || isNaN(value)) {
            return null;
        }

        // Round to maxDecimals
        const rounded = value.toFixed(maxDecimals);

        // Convert to number again → removes trailing zeros
        const cleaned = parseFloat(rounded);

        return new Intl.NumberFormat(getCurrentLocale(), {
            minimumFractionDigits: minDecimals,
            maximumFractionDigits: maxDecimals,
        }).format(cleaned);
    } catch (err) {
        Log.warn('formatNumber: failed to format number', {
            value: value,
            error: err,
        });
        return '';
    }
}
